import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { EmployeePositionId } from '../../../classes/models/employee/Employee';
import { DayOfWeek } from '../../../classes/enums/DayOfWeek';
import { Availability } from '../../../classes/models/employee/Availability';
import {
  AddEmployeeAvailabilityRequest,
  EditEmployeeAvailabilityRequest,
  EmployeeAvailabilityRequest
} from '../../../interfaces/request/EmployeeRequests';
import { Observable } from 'rxjs';
import { StoreEmployeeAvailability } from '../../../classes/models/employee/StoreEmployeeAvailability';
import { map } from 'rxjs/operators';
import { StoreEmployeeAvailabilityResponse } from '../../../interfaces/response/EmployeeResponse';
import { StoreEmployeeAvailabilityAdapter } from '../../../classes/adapters/response/employee/StoreEmployeeAvailabilityAdapter';

/**
 * This service communicates with backend to handle employee availability data management.
 */
@Injectable({
  providedIn: 'root'
})
export class EmployeeAvailabilityService {
  private http = inject(HttpClient);
  private storeEmployeeAvailabilityAdapter = inject(StoreEmployeeAvailabilityAdapter);

  url = environment.apiUrl + '/employees';

  /**
   * Retrieves a list of employee availability records for a specific store.
   * @param storeId an identifier of a store.
   */
  getAvailabilities(storeId: string): Observable<StoreEmployeeAvailability[]> {
    const url = `${this.url}/availabilities`;
    return this.http.get(url, { params: { storeSlug: storeId } }).pipe(
      map((data: StoreEmployeeAvailabilityResponse[]) => data.map(item => this.storeEmployeeAvailabilityAdapter.adapt(item))));
  }

  /**
   * Adds new availabilities to the given employee, starting with the specified date.
   * @param id identifiers of employee and position.
   * @param startDate the date starting from which new availabilities will take effect.
   * @param availabilities new availabilities to add.
   */
  addAvailabilities(id: EmployeePositionId, startDate: string, availabilities: Map<DayOfWeek, Availability>) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/availabilities`;
    const requestBody: AddEmployeeAvailabilityRequest = {
      date: startDate,
      monday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.MONDAY)),
      tuesday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.TUESDAY)),
      wednesday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.WEDNESDAY)),
      thursday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.THURSDAY)),
      friday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.FRIDAY)),
      saturday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.SATURDAY)),
      sunday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.SUNDAY))
    };
    return this.http.post(url, requestBody);
  }

  /**
   * Edits availability for the given employee.
   * @param id identifiers of employee and position.
   * @param startDate the date starting from which availabilities will be edited.
   * @param availabilities availabilities to be edited.
   */
  editAvailability(id: EmployeePositionId, startDate: string, availabilities: Map<DayOfWeek, Availability>) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/availabilities/${startDate}`;
    const requestBody: EditEmployeeAvailabilityRequest = {
      monday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.MONDAY)),
      tuesday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.TUESDAY)),
      wednesday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.WEDNESDAY)),
      thursday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.THURSDAY)),
      friday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.FRIDAY)),
      saturday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.SATURDAY)),
      sunday: this.asAvailabilityRequest(availabilities.get(DayOfWeek.SUNDAY))
    };
    return this.http.put(url, requestBody);
  }

  /**
   * Deletes availability for the given employee.
   * @param id identifiers of employee and position.
   * @param startDate the date starting from which availabilities will be deleted.
   */
  deleteAvailability(id: EmployeePositionId, startDate: string) {
    const url = `${this.url}/${id.parseEmployeeId()}/positions/${id.parsePositionId()}/availabilities/${startDate}`;
    return this.http.delete(url);
  }

  private asAvailabilityRequest(availability: Availability | undefined): EmployeeAvailabilityRequest | null {
    if (!availability) {
      return null;
    }
    return { startTime: availability.startTime, endTime: availability.endTime };
  }
}
